import { apiClient } from '../../core/api';

export type PublishedStatus = 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'REPUBLISHED' | 'DELETED';

// Effect data (clicks / conversions / revenue) is intentionally absent — conversion
// tracking is out of this release per PRD v1.1 #9.
export interface PublishedContent {
  id: string;
  draftId?: string | null;
  title?: string | null;
  /** Published body (markdown/HTML) — snapshotted from the draft; editable in the preview modal (#8). */
  body?: string | null;
  channel: string;
  externalUrl?: string | null;
  status: PublishedStatus;
  failureReason?: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

const basePath = (userId: string) =>
  `/digital-staff/published-content/${encodeURIComponent(userId)}`;

/** How long the published list may take. The client default is 10 s; on dev under load this
 *  list took 30 s (2026-09-08), and the Hub then showed an empty pool over real content. */
export const PUBLISHED_LIST_TIMEOUT_MS = 60_000;

export async function listPublishedContent(
  userId: string,
  options?: { timeoutMs?: number }
): Promise<PublishedContent[]> {
  const response = await apiClient.chatbotGet<PublishedContent[]>(basePath(userId), undefined, {
    timeout: options?.timeoutMs ?? PUBLISHED_LIST_TIMEOUT_MS,
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function updatePublishedContent(
  userId: string,
  publishedId: string,
  payload: { title?: string; body?: string }
): Promise<PublishedContent> {
  const response = await apiClient.chatbotPut<PublishedContent>(
    `${basePath(userId)}/${encodeURIComponent(publishedId)}`,
    payload
  );
  return response.data;
}

export async function republishContent(
  userId: string,
  publishedId: string
): Promise<PublishedContent> {
  const response = await apiClient.chatbotPost<PublishedContent>(
    `${basePath(userId)}/${encodeURIComponent(publishedId)}/republish`,
    {}
  );
  return response.data;
}

export async function deletePublishedContent(userId: string, publishedId: string): Promise<void> {
  await apiClient.chatbotDelete(`${basePath(userId)}/${encodeURIComponent(publishedId)}`);
}
